import {useState, useCallback, useEffect, useMemo} from 'react';
import produce from 'immer';
import get from 'lodash/get';
import {useTranslation} from 'react-i18next';
import {useSMCSnackbar} from '../../../utils/hooks';
import {setProperty, getErrorCode, isStringI18n, extractI18nString} from '../../../utils/utils';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiErrorCodes, ErrorCodes} from '../../../api/consts';
import {SessionDataGetSchema} from '../../../api/DBSchema/SessionData';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import store from '../../../store';
import {push} from 'connected-react-router';
import {SMCPaths, helpContextPageUrl} from '../../../conf/Routes';
import {UserGetSchema} from '../../../api/DBSchema/User';
import UserGeneral from './InnerPages/UserGeneral';
import UserExpiration from './InnerPages/UserExpiration';
import generalIcon from '../../../assets/images/general.png';
import hourglassIcon from '../../../assets/images/hourglass.png';
import megaphoneIcon from '../../../assets/images/megaphone.png';
import padlockIcon from '../../../assets/images/padlock.png';
import pageIcon from '../../../assets/images/page.png';
import {connectedIcon, disconnectedIcon} from '../../../assets/images/gateway';
import UserContact from './InnerPages/UserContact';
import UserTwoStepAuth from './InnerPages/UserTwoStepAuth';
import UserStatus from './InnerPages/UserStatus';
import UserCustomFields from './InnerPages/UserCustomFields';


export function useUserEditor(userId, defaultChosenPageId) {
    const { t } = useTranslation();
    const { enqueueErrorSnackbar, enqueueLogoutSnackbar, enqueueSuccessSnackbar } = useSMCSnackbar();
    const { getErrorMessage, getMessagesArrayByPages } = useSMCErrorHandler();

    const [origUserObject, setOrigUserObject] = useState({});
    const [userObject, setUserObject] = useState({});

    // User uses roles, get all available roles that he may use
    const [availableRoles, setAvailableRoles] = useState([]);
    const [minimumPortalPasswordLength, setMinimumPortalPasswordLength] = useState();
    const [maximumPortalPasswordLength, setMaximumPortalPasswordLength] = useState();

    const [isPageDataLoaded, setIsPageDataLoaded] = useState(false);
    const [isInnerPageLoading, setIsInnerPageLoading] = useState(false);
    const [isPageLoadFailed, setIsPageLoadFailed] = useState(false);
    const [errorMessagesByPageList, setErrorMessagesByPageList] = useState(null);

    const [isObjectDirty, setIsObjectDirty] = useState(false);

    const [chosenPageId, setChosenPageId] = useState(defaultChosenPageId || 'general');
    
    const statusIcon = useMemo(() => {
        if (userObject && userObject[UserGetSchema.pages.status.pageId]) {
            const userStatusList = userObject[UserGetSchema.pages.status.pageId][UserGetSchema.pages.status.statusDataList];
            if (Array.isArray(userStatusList) && userStatusList.length > 0) {
                return connectedIcon;
            } else {
                return disconnectedIcon;
            }
        } else {
            return disconnectedIcon;
        }
    }, [userObject]);

    // Does user have custom field page in editor
    const hasCustomFieldPage = useMemo(() => {
        return !!get(userObject, [UserGetSchema.pages.customFields.pageId, UserGetSchema.pages.customFields.customFieldOne]);
    }, [userObject]);
    
    const editorPages = useMemo(() => ([
        { id: UserGetSchema.pages.general.pageId,      title: t('SMP.GENERAL'),                                 icon: generalIcon,   pageElement: UserGeneral,                                 helpId: helpContextPageUrl.systemUserGeneral },
        { id: UserGetSchema.pages.expiration.pageId,   title: t('SMP.USERS.TABLE.INNER_MENU.EXPIRATION'),       icon: hourglassIcon, pageElement: UserExpiration,                              helpId: helpContextPageUrl.systemUserExpiration },
        { id: UserGetSchema.pages.contact.pageId,      title: t('SMP.USERS.TABLE.INNER_MENU.CONTACT'),          icon: megaphoneIcon, pageElement: UserContact,                                 helpId: helpContextPageUrl.systemUserContact },
        { id: UserGetSchema.pages.customFields.pageId, title: t('SMC_SETTINGS_CUSTOM'),                         icon: pageIcon,      pageElement: UserCustomFields, hide: !hasCustomFieldPage, helpId: helpContextPageUrl.userCustomFields },
        { id: UserGetSchema.pages.twoStepAuth.pageId,  title: t('SMP.TREE_NODE_INNER.TWO_STEP_AUTHENTICATION'), icon: padlockIcon,   pageElement: UserTwoStepAuth,                             helpId: helpContextPageUrl.showingUsers },
        { id: UserGetSchema.pages.status.pageId,       title: t('SMP.USERS.TABLE.INNER_MENU.STATUS'),           icon: statusIcon,    pageElement: UserStatus,                                  helpId: helpContextPageUrl.systemUserStatus }
    ]), [hasCustomFieldPage, statusIcon, t]);

    const [pageIdToPageName, pageIdToHelpId] = useMemo(() => {
        const _pageIdToPageName = {};
        const _pageIdToHelpId = {};
        const addPagesData = (pagesRoot) => {
            pagesRoot.forEach(page => {
                if (page.id) {
                    _pageIdToPageName[page.id] = page.title;
                    _pageIdToHelpId[page.id] = page.helpId;
                }
                if (page.children) {
                    addPagesData(page.children);
                }
            })
        }
        addPagesData(editorPages);
        return [_pageIdToPageName, _pageIdToHelpId];
    }, [editorPages]);

    const helpId = useMemo(() => pageIdToHelpId[chosenPageId], [pageIdToHelpId, chosenPageId]);

    const fetchUser = useCallback(async () => {
        setIsPageDataLoaded(false);
        try {
            const [fetchUserResp, getSessionDataResp] = await Promise.all([
                Api.fetchSingleObject(ObjectTypes.USER, userId),
                Api.getSessionData({attributes: [
                    SessionDataGetSchema.availableRoles,
                    SessionDataGetSchema.minimumPortalPasswordLength,
                    SessionDataGetSchema.maximumPortalPasswordLength
                ]})
            ]);
            const user = fetchUserResp.data.data.user;
            const availableRoles = getSessionDataResp.data[SessionDataGetSchema.availableRoles];
            const minimumPortalPasswordLength = getSessionDataResp.data[SessionDataGetSchema.minimumPortalPasswordLength];
            const maximumPortalPasswordLength = getSessionDataResp.data[SessionDataGetSchema.maximumPortalPasswordLength];
            // Validate server response
            if (!user || !availableRoles) {
                console.error('Get user response failed:');
                !user && console.error('user response returned empty');
                !availableRoles && console.error('getSessionData returned without availableRoles');
                throw new Error(ErrorCodes.SERVER_ERROR);
            }
            setOrigUserObject(user);
            setUserObject(user);
            setAvailableRoles(availableRoles);
            setMinimumPortalPasswordLength(minimumPortalPasswordLength);
            setMaximumPortalPasswordLength(maximumPortalPasswordLength);
            setIsPageLoadFailed(false);
            setIsObjectDirty(false); // Just fetched, not dirty
        } catch (e) {
            const errorCode = getErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                enqueueLogoutSnackbar(t(errorCode));
            } else {
                enqueueErrorSnackbar(t(errorCode));
            }
            setIsPageLoadFailed(true);
        }
        setIsPageDataLoaded(true);
    }, [userId, enqueueErrorSnackbar, enqueueLogoutSnackbar, t]);

    const setUserData = useCallback((paramPath, newValue) => {
        setIsObjectDirty(true); // Data changed, object is dirty
        setUserObject(userObj => produce(userObj, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const verifyData = useCallback(() => {
        setIsInnerPageLoading(true);
        Api.verifySingleObject(ObjectTypes.USER, userId, userObject)
        .then(() => {
            // On no error, clear error message
            setErrorMessagesByPageList(null);
        })
        .catch((error) => {
            const errorMessageArrayByPage = getMessagesArrayByPages(error, pageIdToPageName);
            if (errorMessageArrayByPage) {
                setErrorMessagesByPageList(errorMessageArrayByPage);
            }
        })
        .finally(() => {
            setIsInnerPageLoading(false);
        });
    }, [userId, getMessagesArrayByPages, userObject, pageIdToPageName]);

    const onPageChange = useCallback((pageId) => {
        verifyData();
        setChosenPageId(pageId);
    }, [verifyData]);

    const saveButtonOnClick = useCallback(() => {
        Api.updateSingleObject(ObjectTypes.USER, userId, userObject)
        .then(() => {
            setIsPageDataLoaded(false);
            setErrorMessagesByPageList(null);
            fetchUser();
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_SAVED_OK'));
        })
        .catch((error) => {
            const errorMessage = getErrorMessage(error);
            alert(errorMessage);
        })
        .finally(() => {
            setIsPageDataLoaded(true);
        });
    }, [userId, getErrorMessage, userObject, enqueueSuccessSnackbar, t, fetchUser]);

    const deleteButtonOnClick = useCallback(() => {
        if (window.confirm(t('OBJECT_EDITOR_OBJ_DELETE', [userId] ))) {
            Api.deleteObjects(ObjectTypes.USER, [userId])
            .then(res => {
                const deleteResult = res.data.results[0];
                if (deleteResult.success) {
                    setIsObjectDirty(false); // Set as false to not trigger dirty modal
                    store.dispatch(push(SMCPaths.users));
                    enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_DELETE_OK', [userId]));
                } else {
                    const deleteErrorCode = deleteResult.errorCode;
                    let deleteErrorParams;
                    if (deleteResult.errorParams) {
                        deleteErrorParams = deleteResult.errorParams.map(param => (
                            isStringI18n(param)) ? t(extractI18nString(param)) : param
                        );
                    }
                    enqueueErrorSnackbar(t(extractI18nString(deleteErrorCode), deleteErrorParams));
                }
            })
            .catch(err => {
                const errorCode = getErrorMessage(err);
                enqueueErrorSnackbar(t(errorCode));
            });
        }
    }, [userId, getErrorMessage, enqueueSuccessSnackbar, enqueueErrorSnackbar, t]);

    const revertButtonOnClick = useCallback(() => {
        setUserObject(origUserObject);
        setIsObjectDirty(false); // Is not dirty, object is reset to originl values
        setErrorMessagesByPageList(null);
    }, [origUserObject]);

    // Fetch user and roles on loading of page
    useEffect(() => {
        fetchUser();
    }, [fetchUser]);
    
    return {
        userObject,
        isObjectDirty,
        editorPages,
        helpId,
        origUserObject,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setUserData,
        saveButtonOnClick,
        deleteButtonOnClick,
        revertButtonOnClick,
        reloadPageData: fetchUser,
        availableRoles,
        minimumPortalPasswordLength,
        maximumPortalPasswordLength
    };
}